
import { Component, Prop, Vue, Watch, Emit } from "vue-property-decorator";
import {
  Emotion,
  EmotionData,
  FormatData,
  DataPairs,
  PlotLine,
  Scene,
  ScenePlotLine,
  Subject,
  SubjectsIdsByGender,
  SubjectsDataByGender,
  Series,
  SubjectsIdsByAgeGroup,
  SubjectsDataByAgeGroup,
  EmotionColors,
} from "../interfaces/Interfaces";
import ExportVideoDialog from "./ExportVideoDialog.vue";
import HighchartsVue from "highcharts-vue";
import { Chart } from "highcharts-vue";
import { mapGetters, mapMutations } from "vuex";
import HC_exporting from "highcharts/modules/exporting";
import Highcharts, { chart } from "highcharts";

HC_exporting(Highcharts);
Vue.use(HighchartsVue);

@Component({
  components: {
    ExportVideoDialog,
    highcharts: Chart,
  },
  computed: {
    ...mapGetters("scenes", {
      getScenes: "getScenes",
    }),
    ...mapGetters("stimulus", {
      getSubjectsByGender: "getSubjectsByGender",
    }),
    ...mapGetters("stimulus", {
      getSubjectsByAgeGroup: "getSubjectsByAgeGroup",
    }),
    ...mapGetters("stimulus", {
      getSubjectsByFilter: "getSubjectsByFilter",
    }),
    ...mapGetters("stimulus", {
      getVideoId: "getVideoId",
    }),
    ...mapGetters("stimulus", {
      getProjectId: "getProjectId",
    }),
    ...mapGetters("analysisSettings", {
      getYAxisMaxValue: "getYAxisMaxValue",
    }),
    ...mapGetters("controller", {
      isMultiselection: "isMultiselection",
    }),
  },
  methods: {
    ...mapMutations("controller", {
      enableMultiselection: "enableMultiselection",
      disableMultiselection: "disableMultiselection",
    }),
  },
})
export default class DisplayAnalysisGraph extends Vue {
  @Prop() private emotion!: Emotion;
  @Prop() private emotions!: Emotion[];
  @Prop() private selectedMode!: string;
  @Prop() private yAxisMaxValue?: number | null;
  @Prop() private videoTimestamp!: number | null;
  @Prop() private isVideoPlaying!: boolean;
  @Prop() private showScenes!: boolean;
  @Prop() private removeNoise!: boolean;
  @Prop() private genderSplit!: boolean;
  @Prop() private ageGroupSplit!: boolean;
  @Prop() private filterSplit!: number;
  @Prop() private multipleEmotions!: string[];
  @Prop() private isClientMode!: boolean;
  @Prop() private showExportVideo!: boolean;

  private chartOptions: any;
  private gauss: number[] = [
    0.010798193,
    0.015790032,
    0.022184167,
    0.029945493,
    0.038837211,
    0.048394145,
    0.057938311,
    0.066644921,
    0.073654028,
    0.078208539,
    0.079788456,
    0.078208539,
    0.073654028,
    0.066644921,
    0.057938311,
    0.048394145,
    0.038837211,
    0.029945493,
    0.022184167,
    0.015790032,
    0.010798193,
  ];
  private getScenes!: Scene[] | [];
  private getSubjectsByGender!: SubjectsIdsByGender;
  private getSubjectsByAgeGroup!: SubjectsIdsByAgeGroup;
  private getSubjectsByFilter!: any;
  private getProjectId!: string | [];
  private getYAxisMaxValue!: number;
  private isMultiselection!: boolean;
  private genderColorSwapProjectId: string =
    "8ba01a4b-7c44-45d0-b88b-ae6bde42d721";
  private emotionsBenchmarkValue: number = 0.3;
  private enableMultiselection!: () => boolean;
  private disableMultiselection!: () => boolean;
  private selectedEmotion!: Emotion;
  private singleEmotionSelected: boolean = false;

  private returnScenesList(): Scene[] {
    return this.getScenes;
  }

  constructor(options?: any) {
    super(options);
    const that = this;

    this.chartOptions = {
      chart: {
        type: "area",
        height: "420",
        marginTop: 5,
        marginLeft: 55,
        marginRight: 20,
        events: {
          render: function() {
            let chart = this as any,
              renderer = chart.renderer;

            if (that.showScenes) {
              if (!chart.imagesStrip) {
                chart.imagesStrip = renderer.g("imagesStrip").add();
                renderer
                  .label("Scenes: ", 5, 0)
                  .attr({})
                  .add(chart.imagesStrip);

                const scenesList = that.returnScenesList();
                scenesList.forEach((scene: Scene, index: number): void => {
                  let url: string;
                  if (scene.frame) url = scene.frame;
                  else url = "";

                  let width: number;
                  let height: number;
                  let xOffSet: number;
                  if (scenesList.length < 8) {
                    width = 120;
                    height = 70;
                    xOffSet = 130 * index + 65;
                  } else if (scenesList.length >= 8 && scenesList.length < 11) {
                    width = 80;
                    height = 50;
                    xOffSet = 90 * index + 65;
                  } else {
                    width = 65;
                    height = 40;
                    xOffSet = 70 * index + 65;
                  }
                  //Renders the image's index label
                  renderer
                    .label(index + 1, xOffSet + width / 2 - 5, 0)
                    .attr({ paddingTop: 10 })
                    .add(chart.imagesStrip);

                  //Renders the image
                  renderer
                    .image(url, xOffSet, 20, width, height)
                    .add(chart.imagesStrip);
                });
              } else return;
            } else {
              if (chart.imagesStrip) {
                chart.imagesStrip.destroy();
                chart.imagesStrip = false;
              } else return;
            }
          },
        },
      },
      credits: {
        enabled: false,
      },
      boost: {
        enabled: false,
      },
      title: {
        text: "",
      },
      legend: {
        enabled: true,
        layout: "horizontal",
      },
      series: [],
      plotOptions: {
        series: {
          turboThreshold: 0,
          lineWidth: 1,
          stickyTracking: true,
          states: {
            hover: {
              enabled: true,
              lineWidth: 2,
            },
            inactive: {
              opacity: 1,
            },
          },
          point: {
            events: {
              mouseOver: function(): void {
                if (!that.isVideoPlaying) {
                  that.setTimestamp((this as any).x);
                }
              },
            },
          },
          events: {
            legendItemClick: function() {
              let series = this as any;
              that.handleLegendClick(series.index, !series.visible);
            },
          },
          marker: {
            enabled: false,
          },
        },
        line: {
          marker: {
            enabled: false,
          },
        },
        area: {
          fillColor: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1,
            },
            stops: [
              [0, "red"],
              [1, "white"],
            ],
          },
        },
      },
      tooltip: {
        valueDecimals: 2,
      },
      yAxis: {
        max: 3,
        title: {
          text: "",
        },
      },
      xAxis: {
        labels: {
          formatter: function(): string {
            return (this as any).value / 1000 + "s";
          },
        },
        title: {
          text: "",
        },
        plotLines: [
          {
            color: "gray",
            dashStyle: "ShortDot",
            value: null,
            width: 2,
            zIndex: 5,
          },
        ],
      },
      exporting: {
        chartOptions: {
          chart: {
            type: "area",
            height: "320",
            marginTop: 75,
            marginLeft: 55,
            marginRight: 10,
            events: {
              render: function() {
                let chart = this as any,
                  renderer = chart.renderer;

                if (that.showScenes) {
                  chart.imagesStrip = renderer.g("imagesStrip").add();
                  renderer
                    .label("Scenes: ", 5, 0)
                    .attr({ paddingTop: 20 })
                    .css({ fontSize: "10px" })
                    .add(chart.imagesStrip);

                  const scenesList = that.returnScenesList();
                  scenesList.forEach((scene: Scene, index: number): void => {
                    let url: string;
                    if (scene.frame) url = scene.frame;
                    else
                      url =
                        "https://gdsit.cdn-immedia.net/2015/03/Camaleonte-8.jpg";

                    let width: number;
                    let height: number;
                    let xOffSet: number;
                    if (scenesList.length < 8) {
                      width = 70;
                      height = 40;
                      xOffSet = 75 * index + 60;
                    } else if (
                      scenesList.length >= 8 &&
                      scenesList.length < 11
                    ) {
                      width = 49;
                      height = 30;
                      xOffSet = 54 * index + 60;
                    } else {
                      width = 38;
                      height = 25;
                      xOffSet = 41 * index + 60;
                    }

                    renderer
                      .label(index + 1, xOffSet + width / 2 - 5, 2)
                      .css({ fontSize: "8px" })
                      .add(chart.imagesStrip);

                    renderer
                      .image(url, xOffSet, 20, width, height)
                      .add(chart.imagesStrip);
                  });
                } else {
                  if (chart.imagesStrip) {
                    chart.imagesStrip.destroy();
                    chart.imagesStrip = false;
                  } else return;
                }
              },
            },
          },
          xAxis: [
            {
              labels: {
                style: {
                  fontSize: "8px",
                },
                formatter: function(): string {
                  return (this as any).value / 1000 + "s";
                },
              },
            },
          ],
          yAxis: [
            {
              labels: {
                style: {
                  fontSize: "8px",
                },
              },
              title: {
                text: "",
              },
              max: 3,
            },
          ],
          legend: {
            itemStyle: {
              fontSize: "8px",
            },
            layout: "horizontal",
            padding: 4,
          },
        },
      },
    };
  }

  //Scales the score to a max of 3 points, 3 being 0.3

  private scaleData(value: number): number {
    const maxScore: number = 0.3;
    const scaleMeasure: number = 3;

    const scaledToMaxValue = value / maxScore;
    const scaledToMeasure = scaledToMaxValue * scaleMeasure;

    return Number(scaledToMeasure.toFixed(6));
  }

  // Functions to reduce series data noise
  private getArrayOfSurroundingValues(index: number, data: any) {
    let i: number;
    let array: (null | number)[] = [];
    for (i = 0; i <= 10; i++) {
      const value: DataPairs = data[index - 10 + i];
      if (value) array.push(value["y"]);
      else array.push(null);
    }
    for (i = 0; i < 10; i++) {
      const value: DataPairs = data[index + 1 + i];
      if (value) array.push(value["y"]);
      else array.push(null);
    }
    return array;
  }

  private weightSurroundingValues(
    values: (null | number)[],
    weights: number[],
  ) {
    const weightedValues = values.map((v: null | number, i: number):
      | number
      | null => {
      if (typeof v === "number") return v * weights[i];
      else return null;
    });
    return weightedValues;
  }

  private getGaussSum(values: (null | number)[]): number {
    let sum = 0;
    this.gauss.map((v: number, i: number) => {
      if (typeof values[i] === "number") sum += v;
    });
    return sum;
  }

  private reducePointNoise(value: number, index: number, data: any) {
    const surroundingValues: (
      | null
      | number
    )[] = this.getArrayOfSurroundingValues(index, data);
    const weightedValues: (null | number)[] = this.weightSurroundingValues(
      surroundingValues,
      this.gauss,
    );
    const cleanArr: number[] = weightedValues.filter(
      (v: number | null): v is number => v !== null,
    );
    const sum: number = cleanArr.reduce(
      (v: number, sum: number): number => sum + v,
      0,
    );
    const gaussSum: number = this.getGaussSum(weightedValues);
    return sum / gaussSum;
  }

  private calculateReduceNoise(data: any) {
    data.map((sample: DataPairs, index: number) => {
      const newValue = this.reducePointNoise(sample.y, index, data);
      sample["y"] = newValue;
    });
    return data;
  }

  //Filters data for specific fields
  private filterDataForSpecificField(
    emotion: Emotion,
    ids: string[],
    label: string,
  ): Emotion {
    const filteredData: EmotionData[] = emotion.data.map(
      (timestamp: EmotionData): EmotionData => {
        let newObj: EmotionData = { Timestamp: timestamp["Timestamp"] };
        ids.map((id: string): void => (newObj[id] = timestamp[id]));
        return newObj;
      },
    );
    const name: string = emotion["type"] + " / " + label;
    const filteredEmotion: Emotion = {
      type: name,
      data: filteredData,
    };
    return filteredEmotion;
  }

  private splitDataByGender(): SubjectsDataByGender {
    let emotion = !this.isClientMode ? this.emotion : this.selectedEmotion;
    if (!emotion) {
      emotion = this.emotion;
    }
    const gendersIds: SubjectsIdsByGender = this.getSubjectsByGender;

    const malesData: Emotion = this.filterDataForSpecificField(
      emotion,
      gendersIds["males"],
      `Males / ${gendersIds["males"].length} subjects`,
    );
    const femalesData: Emotion = this.filterDataForSpecificField(
      emotion,
      gendersIds["females"],
      `Females / ${gendersIds["females"].length} subjects`,
    );
    const splitData: SubjectsDataByGender = {
      males: malesData,
      females: femalesData,
    };
    return splitData;
  }

  private splitDataByAgeGroup(): SubjectsDataByAgeGroup {
    let emotion = !this.isClientMode ? this.emotion : this.selectedEmotion;
    if (!emotion) {
      emotion = this.emotion;
    }
    const ageGroupsIds: SubjectsIdsByAgeGroup = this.getSubjectsByAgeGroup;
    const firstAgeGroupData: Emotion = this.filterDataForSpecificField(
      emotion,
      ageGroupsIds["firstGroup"],
      `Age: 18-35 / ${ageGroupsIds["firstGroup"].length} subjects`,
    );
    const secondAgeGroupData: Emotion = this.filterDataForSpecificField(
      emotion,
      ageGroupsIds["secondGroup"],
      `Age: 35-50 / ${ageGroupsIds["secondGroup"].length} subjects`,
    );
    const thirdAgeGroupData: Emotion = this.filterDataForSpecificField(
      emotion,
      ageGroupsIds["thirdGroup"],
      `Age: 50-70 / ${ageGroupsIds["thirdGroup"].length} subjects`,
    );
    const splitData: SubjectsDataByAgeGroup = {
      firstGroup: firstAgeGroupData,
      secondGroup: secondAgeGroupData,
      thirdGroup: thirdAgeGroupData,
    };
    return splitData;
  }

  private splitDataByFilter(): any {
    let emotion = !this.isClientMode ? this.emotion : this.selectedEmotion;
    if (!emotion) {
      emotion = this.emotion;
    }

    const filterIds: any = this.getSubjectsByFilter(this.filterSplit);

    const filteredData: any = {};
    for (const filt in filterIds) {
      filteredData[filt] = this.filterDataForSpecificField(
        emotion,
        filterIds[filt],
        `${filt} - ${filterIds[filt].length} subjects`,
      );
    }

    return filteredData;
  }

  //Prepares data for highchart series
  private getAverage(o: EmotionData): DataPairs {
    let sum: number = 0;
    let count: number = 0;
    Object.keys(o).map((k: string): void => {
      if (k !== "Timestamp" && Number(o[k]) != -1) {
        count += 1;
        sum += Number(o[k]);
      }
    });
    // const avg: number = sum / Object.keys(o).length;
    const avg: number = sum / count;

    let t: number = 0;
    if (o["Timestamp"]) t = o["Timestamp"];

    const pairs = {
      x: t,
      y: this.scaleData(avg),
    };
    return pairs;
  }

  private getData(emotion: Emotion): DataPairs[] {
    let data: DataPairs[] = [];
    emotion.data.map((t: EmotionData): void => {
      const avg: DataPairs = this.getAverage(t);
      data.push(avg);
    });
    if (this.removeNoise) data = this.calculateReduceNoise(data);
    return data;
  }

  private getEmotionObject(emotion: string): Emotion | undefined {
    const object = this.emotions.find(
      (e: Emotion): boolean => e.type === emotion,
    );
    if (object) return object;
    else return undefined;
  }

  // Codes picked from vuetify color palette

  private emotionsColors: EmotionColors = {
    Happiness: "#43A047", // green
    Sadness: "#D32F2F", // dark-red
    Anger: "#FBC02D", // yellow
    Contempt: "#FB8C00", // orange
    Surprise: "#1E88E5", // blue
    Disgust: "#8D6E63", // light-brown
    Fear: "#212121", // black
    Neutral: "#BDBDBD", // grey
    Valence: "#637079", // dark-gray
    Negative: "#E53935", // red,
    Attention: "#673AB7", // deep purple
  };

  private getCatColor(split: string, cat: string): string {
    if (split === "filterSplit") {
      const blueTones = [
        ["#203240"],
        ["#203240", "#749ABA"],
        ["#203240", "#426684", "#BACDDC"],
        ["#203240", "#426684", "#749ABA", "#BACDDC"],
      ];

      const redTones = [
        ["#EC615F"],
        ["#EC615F", "#F8BFBE"],
        ["#EC615F", "#F3A09E", "#FBE0DF"],
        ["#C21D18", "#EC615F", "#F3A09E", "#FBE0DF"],
      ];

      let tone = cat.split("_")[0];
      let group_nr = parseInt(cat.split("_")[1]);
      let idx = parseInt(cat.split("_")[2]);

      let color = "";
      try {
        if (tone === "blue") {
          color = blueTones[group_nr][idx];
        } else {
          color = redTones[group_nr][idx];
        }
      } catch {
        color = "#" + Math.floor(Math.random() * 16777215).toString(16);
      }

      return color;
    }

    if (split === "genderSplit") {
      const projectId: string = String(this.getProjectId || "").trim();
      const targetProjectId: string = String(
        this.genderColorSwapProjectId || "",
      ).trim();
      const swapGenderColors: boolean =
        targetProjectId.length > 0 &&
        projectId.toLowerCase() === targetProjectId.toLowerCase();
      switch (cat) {
        case "males":
          return swapGenderColors ? "#ec625f" : "#203240";
          break;
        case "females":
          return swapGenderColors ? "#203240" : "#ec625f";
          break;
        default:
          return "gray";
      }
    } else {
      switch (cat) {
        case "firstGroup":
          return "#BACDDC";
          break;
        case "secondGroup":
          return "#ec625f";
          break;
        case "thirdGroup":
          return "#203240";
          break;
        default:
          return "gray";
      }
    }
  }

  private formatData(): void {
    const series: Series[] = [];
    const createAndPushSeries = (
      emotion: Emotion,
      visible: boolean = true,
    ): void => {
      const values: DataPairs[] = this.getData(emotion);
      const color: string = this.emotionsColors[emotion.type];
      const data: Series = {
        name: emotion.type,
        data: values,
        color: color,
        visible: visible,
      };
      series.push(data);
    };
    const createAndPushCatSeries = (
      emotion: Emotion,
      split: string,
      cat: string,
    ): void => {
      const values: DataPairs[] = this.getData(emotion);
      let color: string = this.getCatColor(split, cat);

      const data: Series = {
        name: emotion.type,
        data: values,
        color: color,
        visible: true,
      };
      series.push(data);
    };
    if (
      !this.genderSplitState &&
      !this.ageGroupSplitState &&
      this.filterSplitState === -1 &&
      this.singleEmotionSelected
    ) {
      this.multipleEmotions.forEach((e: string): void => {
        const emotionObject: Emotion | undefined = this.getEmotionObject(e);
        if (emotionObject) {
          let visible = this.selectedEmotion !== emotionObject ? false : true;
          createAndPushSeries(emotionObject, visible);
        }
      });
    } else {
      if (!this.isMultiselection) {
        if (this.genderSplit) {
          const splitGenders: SubjectsDataByGender = this.splitDataByGender();
          createAndPushCatSeries(splitGenders["males"], "genderSplit", "males");
          createAndPushCatSeries(
            splitGenders["females"],
            "genderSplit",
            "females",
          );
        } else if (this.ageGroupSplit) {
          const splitAgeGroups: SubjectsDataByAgeGroup = this.splitDataByAgeGroup();
          createAndPushCatSeries(
            splitAgeGroups["firstGroup"],
            "splitAgeGroups",
            "firstGroup",
          );
          createAndPushCatSeries(
            splitAgeGroups["secondGroup"],
            "splitAgeGroups",
            "secondGroup",
          );
          createAndPushCatSeries(
            splitAgeGroups["thirdGroup"],
            "splitAgeGroups",
            "thirdGroup",
          );
        } else if (this.filterSplit !== -1) {
          console.log("FILTER SPLIT");
          const splitByFilter: any = this.splitDataByFilter();

          const len_of_groups = Math.ceil(
            Object.keys(splitByFilter).length / 2,
          );
          var idx = 0;
          var position = 1;
          for (const filt in splitByFilter) {
            idx += 1;
            let color_type = idx % 2 === 1 ? "red" : "blue";
            createAndPushCatSeries(
              splitByFilter[filt],
              "filterSplit",
              color_type +
                "_" +
                len_of_groups.toString() +
                "_" +
                position.toString(),
            );
            if (idx > 1 && !(idx % 2)) {
              position += 1;
            }
          }
        } else {
          if (this.multipleEmotions.length) {
            var selEmotion = this.chartOptions.series[0].name
              .split("/")[0]
              .trim();
            this.multipleEmotions.forEach((e: string): void => {
              const emotionObject: Emotion | undefined = this.getEmotionObject(
                e,
              );
              if (emotionObject) {
                createAndPushSeries(emotionObject, e === selEmotion);
              }
            });

            // this.enableMultiselection()
            this.singleEmotionSelected = false;

            this.chartOptions.series = series;
            this.setAreaChartType(series);
            return;
          } else {
            createAndPushSeries(this.emotion);
          }
        }
      } else {
        this.multipleEmotions.forEach((e: string): void => {
          const emotionObject: Emotion | undefined = this.getEmotionObject(e);
          if (emotionObject) {
            createAndPushSeries(emotionObject);
          }
        });
      }
    }
    this.chartOptions.series = series;
    // if (this.isMultiselection)

    if (
      this.isMultiselection ||
      this.genderSplit ||
      this.ageGroupSplit ||
      this.filterSplit !== -1 ||
      this.singleEmotionSelected ||
      this.isClientMode
    ) {
      this.setLineChartType();
    } else {
      this.setAreaChartType(series);
    }
  }

  private handleLegendClick(index: number, visible: boolean): void {
    if (!this.isClientMode) {
      return;
    }

    this.chartOptions.series[index].visible = visible;
    const selectedSeries = this.chartOptions.series.filter(
      (el: any) => el.visible,
    );

    const splitActive =
      this.genderSplit || this.ageGroupSplit || this.filterSplit !== -1;

    if (selectedSeries.length === 1) {
      this.disableMultiselection();
      this.singleEmotionSelected = true;
      const e = selectedSeries[0].name;
      const emotionObject: Emotion | undefined = this.getEmotionObject(e);
      if (emotionObject) {
        this.selectedEmotion = emotionObject;
      }
      if (splitActive) {
        this.setLineChartType();
      } else {
        this.setAreaChartType(selectedSeries);
      }

      return;
    } else {
      this.setLineChartType();
    }
    this.enableMultiselection();
    this.singleEmotionSelected = false;
  }

  created(): void {
    if (this.emotion) this.formatData();
  }

  @Watch("getYAxisMaxValue") private setScale(): void {
    if (this.yAxisMaxValue) {
      this.chartOptions.yAxis.max = this.yAxisMaxValue;
      this.chartOptions.exporting.chartOptions.yAxis[0].max = this.yAxisMaxValue;
    } else {
      this.chartOptions.yAxis.max = null;
      this.chartOptions.exporting.chartOptions.yAxis[0].max = null;
    }
  }

  // Watchers to edit series data
  @Watch("removeNoise")
  private triggerRemoveNoise(): void {
    this.formatData();
  }

  @Watch("emotion")
  private changeEmotionData(): void {
    this.formatData();
  }

  @Watch("multipleEmotions")
  private changeMultipleEmotionsData(): void {
    this.formatData();
  }

  @Watch("getProjectId")
  private reactToProjectIdToggle(): void {
    if (this.emotion) {
      this.formatData();
    }
  }

  @Watch("isMultiselection")
  private reactToMultiselectionToggle(): void {
    this.formatData();
  }

  private get genderSplitState(): boolean {
    return this.$store.state.analysisSettings.genderSplit;
  }

  @Watch("genderSplit")
  private reactToGenderSplitToggle(): void {
    this.formatData();
  }

  private get ageGroupSplitState(): boolean {
    return this.$store.state.analysisSettings.ageGroupSplit;
  }

  @Watch("ageGroupSplit")
  private reactToAgeGroupSplitToggle(): void {
    this.formatData();
  }

  private get filterSplitState(): number {
    return this.$store.state.analysisSettings.filterIndex;
  }

  @Watch("filterSplit")
  private reactToFilterSplitToggle(): void {
    this.formatData();
  }

  //Helper functions for rendering images strips and plotlines
  private get videoLengthInMilliseconds(): number {
    const length: number = this.emotions[0].data.length;
    return length * 100;
  }

  private getSceneLabelPosition(
    scenesPlotLines: ScenePlotLine[],
    index: number,
  ): number {
    let sceneEnd: number;
    if (scenesPlotLines.length > index + 1) {
      sceneEnd = scenesPlotLines[index + 1].value;
    } else {
      sceneEnd = this.videoLengthInMilliseconds;
    }
    const horizontalSkew =
      scenesPlotLines[index].value +
      (sceneEnd - scenesPlotLines[index].value) / 2;
    return horizontalSkew;
  }

  private setLineChartType(): void {
    this.chartOptions.chart.type = "line";
    this.chartOptions.exporting.chartOptions.chart.type = "line";
    this.chartOptions.plotOptions.series.lineWidth = 2;
  }

  private setAreaChartType(series: any): void {
    this.chartOptions.chart.type = "area";
    this.chartOptions.exporting.chartOptions.chart.type = "area";
    this.chartOptions.plotOptions.series.lineWidth = 1;
    this.chartOptions.plotOptions.area.fillColor.stops = [
      [0, series[0].color],
      [1, "white"],
    ];
  }

  // Toggles show scenes plotlines on the graph
  @Watch("showScenes") private handleToggleShowScenes(): void {
    let scenes = JSON.parse(JSON.stringify(this.getScenes));
    if (this.showScenes && scenes) {
      const scenesPlotLines: ScenePlotLine[] = scenes.map(
        (scene: Scene, index: number): ScenePlotLine => {
          const scenePlotLine = {
            value: scene.start * 100,
            color: "lightgray",
            dashStyle: "ShortDot",
            width: 1,
          };
          return scenePlotLine;
        },
      );
      const scenesDummyPlotLines: ScenePlotLine[] = scenesPlotLines.map(
        (plotLine: ScenePlotLine, index: number): ScenePlotLine => {
          const scenePlotLine = {
            value: this.getSceneLabelPosition(scenesPlotLines, index),
            color: "lightgray",
            dashStyle: "ShortDot",
            width: 0,
            label: {
              useHTML: true,
              rotation: 0,
              verticalAlign: "top",
              y: 15,
              x: 0,
              text: `<span>${index + 1}<span>`,
            },
          };
          return scenePlotLine;
        },
      );
      const scenesDummyPlotLinesForExport: ScenePlotLine[] = scenesPlotLines.map(
        (plotLine: ScenePlotLine, index: number): ScenePlotLine => {
          const scenePlotLine = {
            value: this.getSceneLabelPosition(scenesPlotLines, index),
            color: "lightgray",
            dashStyle: "ShortDot",
            width: 0,
            label: {
              useHTML: true,
              rotation: 0,
              verticalAlign: "top",
              y: -5,
              x: -3,
              text: `<span>${index + 1}<span>`,
              style: {
                fontSize: "8",
              },
            },
          };
          return scenePlotLine;
        },
      );
      this.chartOptions.xAxis.plotLines = [
        ...this.chartOptions.xAxis.plotLines,
        ...scenesPlotLines,
        ...scenesDummyPlotLines,
      ];
      this.chartOptions.exporting.chartOptions.xAxis[0].plotLines = [
        ...scenesPlotLines,
        ...scenesDummyPlotLinesForExport,
      ];
      this.chartOptions.chart.marginTop = 150;
      this.chartOptions.exporting.chartOptions.chart.marginTop = 80;
      this.chartOptions.chart.height = "420";
      this.chartOptions.exporting.chartOptions.height = "500";
    } else {
      this.chartOptions.xAxis.plotLines = this.chartOptions.xAxis.plotLines.slice(
        0,
        1,
      );
      this.chartOptions.exporting.chartOptions.xAxis[0].plotLines = [];
      this.chartOptions.chart.marginTop = 5;
      this.chartOptions.exporting.chartOptions.chart.marginTop = 20;
      this.chartOptions.chart.height = "320";
      this.chartOptions.exporting.chartOptions.height = "250";
    }
  }

  @Emit("graphTimestamp") public emitTimestamp(timestamp: number) {
    return timestamp;
  }

  // Updates the plotLine calls emitTimestamp if video is not playing
  public setTimestamp(timestamp: number): void {
    this.chartOptions.xAxis.plotLines[0].value = timestamp;
    if (!this.isVideoPlaying) {
      this.emitTimestamp(timestamp);
    }
  }

  //Watches for changes in the video's timestamp progression
  @Watch("videoTimestamp") private setGraphTimestamp(): void {
    if (this.videoTimestamp) {
      const timestampInMilliseconds: number = this.videoTimestamp * 1000;
      this.setTimestamp(timestampInMilliseconds);
    }
  }

  //Checks for showScenes on component mount
  private mounted(): void {
    this.handleToggleShowScenes();
    this.triggerRemoveNoise();
    this.reactToGenderSplitToggle();
    this.reactToAgeGroupSplitToggle();
    this.reactToFilterSplitToggle();
  }
}
